/**
 * The storage location picker's rules (z8nrz7fqfy): where a product can go,
 * read the way someone walks to it — building, then room, then the storage
 * unit, then the slot.
 *
 * Pure, so the grouping is tested; the tree is `GET /facility/locations` and
 * the units are the capacity summary, already narrowed to the ones that can
 * take the item.
 */
import type { LocationTree } from '@/api/facilityApi'
import type { ApiStorageNode, CapacitySummaryRow } from '@/types/storage'
import { allRooms, PLACE_SEPARATOR } from './facilityLocation'
import { isTempLocation } from './productStorage'
import { formatLocation, isOccupiable, pathSegments } from './storageTree'

/** Rooms on no floor, so in no building. */
export const NO_BUILDING = 'no-building'
/** Units in no room at all. */
export const NO_ROOM = 'no-room'

export interface PickerRoom {
  key: string
  /** The Room ID. */
  label: string
  /** What the room is ("Cryo Storage"), when recorded. */
  detail: string | null
  floor: string | null
  /** The room itself is marked out of service. Its units are still listed. */
  outOfService: boolean
  units: CapacitySummaryRow[]
  /** Free slots in its units that can take stock now: an unavailable unit's do not count. */
  free: number
}

export interface PickerBuilding {
  key: string
  label: string
  /** The site it is on. */
  detail: string | null
  rooms: PickerRoom[]
  free: number
  unitCount: number
}

/** A temporary or staging unit: offered last, and marked. */
export function isTemporaryUnit(unit: Pick<CapacitySummaryRow, 'code' | 'name'>): boolean {
  return isTempLocation({ code: unit.code, name: unit.name, path: unit.code })
}

/**
 * Why a unit cannot take stock right now, in words for the person picking, or
 * null when it can. Out of service and mid-maintenance are what the server
 * refuses a put-away for (`unit-availability`), so the picker shows these units
 * but never lets one be chosen.
 */
export function unitUnavailableMessage(
  unit: Pick<CapacitySummaryRow, 'code' | 'name' | 'unavailable'>,
): string | null {
  if (!unit.unavailable) return null
  const name = unit.name || unit.code
  const detail = unit.unavailable.detail?.trim()
  const state =
    unit.unavailable.reason === 'MAINTENANCE_IN_PROGRESS' ? 'under maintenance' : 'out of service'
  return `${name} is ${state}${detail ? ` — ${detail}` : ''}. Nothing can be stored in it until it is back in service; pick another unit.`
}

/** In service first, then temporary or staging units, then the ones that cannot take stock. */
const unitOrder = (u: CapacitySummaryRow) => (u.unavailable ? 2 : isTemporaryUnit(u) ? 1 : 0)
const byOfferOrder = (a: CapacitySummaryRow, b: CapacitySummaryRow) => unitOrder(a) - unitOrder(b)

/**
 * The units grouped by building and room, in the facility's own order. Only
 * places holding at least one of `units` appear — the picker offers where the
 * item can go, not the whole site. Rooms on no floor come after the buildings,
 * and units in no room last.
 */
export function groupUnitsByPlace(
  tree: LocationTree | undefined,
  units: CapacitySummaryRow[],
): PickerBuilding[] {
  const roomOfEquipment = new Map<string, string>()
  const rooms = tree ? allRooms(tree) : []
  for (const r of rooms) for (const u of r.units) roomOfEquipment.set(u.id, r.id)

  const unitsByRoom = new Map<string, CapacitySummaryRow[]>()
  for (const unit of units) {
    const roomId = (unit.equipmentId && roomOfEquipment.get(unit.equipmentId)) || NO_ROOM
    const list = unitsByRoom.get(roomId)
    if (list) list.push(unit)
    else unitsByRoom.set(roomId, [unit])
  }

  const out: PickerBuilding[] = []
  const add = (building: Omit<PickerBuilding, 'free' | 'unitCount'>) => {
    if (building.rooms.length === 0) return
    out.push({
      ...building,
      free: building.rooms.reduce((n, r) => n + r.free, 0),
      unitCount: building.rooms.reduce((n, r) => n + r.units.length, 0),
    })
  }
  const roomOf = (
    id: string,
    label: string,
    detail: string | null,
    floor: string | null,
    outOfService = false,
  ): PickerRoom | null => {
    const held = unitsByRoom.get(id)
    if (!held) return null
    const sorted = [...held].sort(byOfferOrder)
    const free = sorted.reduce((n, u) => n + (u.unavailable ? 0 : u.remaining), 0)
    return { key: id, label, detail, floor, outOfService, units: sorted, free }
  }

  for (const f of tree?.facilities ?? []) {
    for (const b of f.buildings) {
      const inBuilding: PickerRoom[] = []
      for (const fl of [...b.floors].sort((x, y) => x.level - y.level)) {
        for (const r of fl.rooms) {
          const room = roomOf(r.id, r.label, r.name, fl.name, r.status === 'OUT_OF_SERVICE')
          if (room) inBuilding.push(room)
        }
      }
      add({ key: b.id, label: b.name, detail: f.name || f.code, rooms: inBuilding })
    }
  }
  add({
    key: NO_BUILDING,
    label: 'Rooms not in a building',
    detail: null,
    rooms: (tree?.unmappedRooms ?? []).flatMap((r) => {
      const room = roomOf(r.id, r.label, r.name, null)
      return room ? [room] : []
    }),
  })
  const loose = roomOf(NO_ROOM, 'Not in a room', null, null)
  add({ key: NO_ROOM, label: 'Not placed in a room', detail: null, rooms: loose ? [loose] : [] })
  return out
}

/** Where a unit sits in the picker, to open on it. Null when it is not offered. */
export function placeOfUnit(
  buildings: PickerBuilding[],
  unitNodeId: string,
): { building: PickerBuilding; room: PickerRoom } | null {
  for (const building of buildings) {
    for (const room of building.rooms) {
      if (room.units.some((u) => u.nodeId === unitNodeId)) return { building, room }
    }
  }
  return null
}

export interface UnitMatch {
  unit: CapacitySummaryRow
  building: PickerBuilding
  room: PickerRoom
}

/**
 * Units answering a search, with where each stands. A building or room that
 * matches brings all its units, since "Building 1" means everything in it.
 */
export function searchUnits(buildings: PickerBuilding[], query: string): UnitMatch[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const hit = (text: string | null | undefined) => !!text && text.toLowerCase().includes(q)
  const out: UnitMatch[] = []
  for (const building of buildings) {
    const buildingHit = hit(building.label) || hit(building.detail)
    for (const room of building.rooms) {
      const roomHit = buildingHit || hit(room.label) || hit(room.detail) || hit(room.floor)
      for (const unit of room.units) {
        if (roomHit || hit(unit.code) || hit(unit.name)) out.push({ unit, building, room })
      }
    }
  }
  return out
}

export interface SlotGroup {
  /** The container's id. */
  key: string
  /** Where the container is inside the unit ("R3 › B04"); empty for slots directly in it. */
  label: string
  slots: ApiStorageNode[]
}

/**
 * Free slots grouped by the rack or box they are in, in the order they came
 * (the server's: each box in reading order). What a long list of slot paths
 * reads as once the shared part is said once per box.
 */
export function slotGroups(nodes: ApiStorageNode[]): SlotGroup[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const groups = new Map<string, SlotGroup>()
  for (const n of nodes) {
    if (!isOccupiable(n.kind)) continue
    const key = n.parentId ?? ''
    let group = groups.get(key)
    if (!group) {
      const parent = byId.get(key)
      const inside = parent ? pathSegments(parent.path).slice(1).join('/') : ''
      group = { key, label: inside ? formatLocation(inside, PLACE_SEPARATOR) : '', slots: [] }
      groups.set(key, group)
    }
    group.slots.push(n)
  }
  return [...groups.values()]
}
